import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import "./EmailVerification.scss";
import { verifyRecipientEmail } from "../../services/v5Api";
import successTick from "../../assets/images/successTick.svg";
import videoUploadError from "../../assets/images/videoUploadError.svg";
import { useT } from "../../i18n/useT";

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  EXPIRED: "expired",
  INVALID: "invalid",
  ERROR: "error",
};

const LOGO_URL =
  "https://hma-dev-assets.s3.ap-northeast-1.amazonaws.com/logo/sllogo148.png";

const ExpiredIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="email-verification__icon">
    <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" />
    <path
      d="M12 7v5l3 3"
      stroke="#F59E0B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmailVerification = ({ token }) => {
  const t = useT("emailVerification");
  const [status, setStatus] = useState(STATUS.LOADING);
  const [resendRequested, setResendRequested] = useState(false);

  useEffect(() => {
    verifyRecipientEmail(token)
      .then(() => setStatus(STATUS.SUCCESS))
      .catch((err) => {
        const message = err.response?.data?.message || "";
        if (message.toLowerCase().includes("expired")) {
          setStatus(STATUS.EXPIRED);
        } else if (err.response?.status === 404) {
          setStatus(STATUS.INVALID);
        } else if (err.response?.status === 401) {
          setStatus(STATUS.INVALID);
        } else {
          setStatus(STATUS.ERROR);
        }
      });
  }, [token]);

  const renderContent = () => {
    if (status === STATUS.LOADING) {
      return (
        <Box sx={{ py: 4 }}>
          <CircularProgress
            size={48}
            sx={{ color: "var(--primary-color, #1976d2)" }}
          />
          <Typography className="email-verification__body" sx={{ mt: 3 }}>
            {t("verifying")}
          </Typography>
        </Box>
      );
    }

    if (status === STATUS.SUCCESS) {
      return (
        <>
          <div className="email-verification__icon-wrap email-verification__icon-wrap--success">
            <img
              src={successTick}
              alt="Success"
              className="email-verification__icon"
            />
          </div>
          <Typography className="email-verification__title">
            {t("verified_title")}
          </Typography>
          <Typography className="email-verification__body">
            {t("verified_body")}
          </Typography>
          <div className="email-verification__divider" />
          <Typography className="email-verification__hint">
            {t("close_tab")}
          </Typography>
        </>
      );
    }

    if (status === STATUS.EXPIRED) {
      return (
        <>
          <div className="email-verification__icon-wrap email-verification__icon-wrap--expired">
            <ExpiredIcon />
          </div>
          <Typography className="email-verification__title">
            {t("expired_title")}
          </Typography>
          <Typography className="email-verification__body">
            {t("expired_body")}
          </Typography>
          {!resendRequested ? (
            <Button
              variant="contained"
              onClick={() => setResendRequested(true)}
              sx={{
                backgroundColor: "var(--primary-color, #1976d2)",
                borderRadius: "8px",
                textTransform: "none",
                px: 4,
                py: 1.5,
                fontSize: "var(--type-body)",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "var(--primary-color, #1565c0)",
                  filter: "brightness(0.9)",
                },
              }}
            >
              {t("request_new")}
            </Button>
          ) : (
            <>
              <div className="email-verification__divider" />
              <Typography className="email-verification__hint">
                {t("expired_hint")}
              </Typography>
            </>
          )}
        </>
      );
    }

    if (status === STATUS.INVALID) {
      return (
        <>
          <div className="email-verification__icon-wrap email-verification__icon-wrap--error">
            <img
              src={videoUploadError}
              alt="Error"
              className="email-verification__icon"
            />
          </div>
          <Typography className="email-verification__title">
            {t("invalid_title")}
          </Typography>
          <Typography className="email-verification__body">
            {t("invalid_body")}
          </Typography>
        </>
      );
    }

    return (
      <>
        <div className="email-verification__icon-wrap email-verification__icon-wrap--error">
          <img
            src={videoUploadError}
            alt="Error"
            className="email-verification__icon"
          />
        </div>
        <Typography className="email-verification__title">
          {t("error_title")}
        </Typography>
        <Typography className="email-verification__body">
          {t("error_body")}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontSize: "var(--type-body)",
          }}
        >
          {t("try_again")}
        </Button>
      </>
    );
  };

  return (
    <div className="email-verification">
      <div className="email-verification__card">
        <img
          src={LOGO_URL}
          alt="SeniorLife"
          className="email-verification__logo"
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default EmailVerification;
